import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

function syncAttributes(target, source, excludedAttributes = []) {
  const excluded = new Set(excludedAttributes);

  for (const attribute of Array.from(source.attributes)) {
    if (excluded.has(attribute.name)) continue;
    if (target.getAttribute(attribute.name) !== attribute.value) {
      target.setAttribute(attribute.name, attribute.value);
    }
  }

  for (const attribute of Array.from(target.attributes)) {
    if (excluded.has(attribute.name)) continue;
    if (!source.hasAttribute(attribute.name)) {
      target.removeAttribute(attribute.name);
    }
  }
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = async (event) => {
    try {
      await this.#renderUpdatedMarkup(event);
    } catch (error) {
      console.error(error);
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  async #renderUpdatedMarkup(event) {
    const sectionId = this.#sectionId;

    if (!sectionId) return;

    const sectionHtml = event.detail?.data?.sections?.[sectionId] ?? (await sectionRenderer.getSectionHTML(sectionId, false));
    const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const currentDialog = this.querySelector('dialog[ref="dialog"]');
    const currentCartItemsComponent = this.querySelector('cart-items-component');
    const newDialog = Array.from(newSectionHTML.querySelectorAll('dialog')).find((element) => {
      const cartItemsComponent = element.querySelector('cart-items-component');
      return cartItemsComponent?.dataset.sectionId === sectionId;
    });
    const newCartItemsComponent = Array.from(newSectionHTML.querySelectorAll('cart-items-component')).find((element) => {
      return element.dataset.sectionId === sectionId;
    });

    if (
      !(currentDialog instanceof HTMLDialogElement)
      || !(currentCartItemsComponent instanceof HTMLElement)
      || !(newDialog instanceof HTMLDialogElement)
      || !(newCartItemsComponent instanceof HTMLElement)
    ) {
      throw new Error('Updated cart drawer markup not found');
    }

    syncAttributes(currentDialog, newDialog, ['open']);
    morph(currentCartItemsComponent, newCartItemsComponent, { childrenOnly: true });
    currentCartItemsComponent.updatedCallback?.();
    this.updatedCallback();
  }

  get #sectionId() {
    return this.querySelector('cart-items-component')?.dataset.sectionId;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
